import { useState, useRef } from 'react';
import { useStore } from '@/store';
import { commonTimezones } from '@/lib/timezone';
import { Plus, Upload } from 'lucide-react';
import { trackAction } from '@/lib/analytics';
import { getDefaultAvatar, avatarColors, generateAvatarSvg, processAvatarUpload, isCustomAvatar } from '@/lib/avatars';

export default function QuickAddForm() {
  const { addPerson } = useStore();
  
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [avatar, setAvatar] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const dataUrl = await processAvatarUpload(file);
      setAvatar(dataUrl);
      setShowAvatarPicker(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleQuickAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    addPerson({
      name: name.trim(),
      avatar: avatar || getDefaultAvatar(name.trim()),
      timezone,
      location: '',
      workHourStart: 9,
      workHourEnd: 17,
    });

    // Track the action
    trackAction('add_person', { timezone });

    // Reset form
    setName('');
    setAvatar('');
    setTimezone('America/New_York');
  };

  return (
    <div className="bg-white/40 dark:bg-white/5 backdrop-blur-md rounded-lg border border-gray-200/50 dark:border-white/10 p-3">
      <form onSubmit={handleQuickAdd} className="flex gap-2">
        {/* Avatar Button */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowAvatarPicker(!showAvatarPicker)}
            className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center border-2 border-gray-200/50 dark:border-white/10 hover:border-primary transition-colors"
            title="Choose avatar"
          >
            {avatar ? (
              isCustomAvatar(avatar) ? (
                <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <img src={avatar} alt="Avatar" className="w-full h-full" />
              )
            ) : (
              <Plus className="w-5 h-5 text-text-muted" />
            )}
          </button>
          
          {/* Avatar Picker Dropdown */}
          {showAvatarPicker && (
            <div className="absolute top-12 left-0 z-10 bg-bg-light-card dark:bg-bg-card border border-gray-200 dark:border-white/10 rounded-xl p-3 shadow-xl w-72">
              <div className="text-xs text-text-muted mb-2 font-semibold uppercase tracking-wide">Choose Avatar:</div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {avatarColors.map((color, index) => {
                  const avatarSvg = generateAvatarSvg(index);
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setAvatar(avatarSvg);
                        setShowAvatarPicker(false);
                      }}
                      className="w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center transition-all hover:scale-105 opacity-80 hover:opacity-100"
                    >
                      <img src={avatarSvg} alt={color.name} className="w-full h-full" />
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-gray-200 dark:border-white/10 pt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full btn-secondary text-xs py-2 flex items-center justify-center gap-2"
                >
                  <Upload className="w-3 h-3" />
                  {uploading ? 'Uploading...' : 'Upload (max 500KB)'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Name input */}
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Add teammate..."
          className="flex-1 min-w-0 px-4 py-2 bg-white/60 dark:bg-black/20 backdrop-blur-sm border border-gray-200/50 dark:border-white/10 rounded-lg text-sm text-text-light dark:text-text placeholder:text-text-light-muted dark:placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        />

        {/* Timezone selector */}
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="w-48 px-4 py-2 bg-white/60 dark:bg-black/20 backdrop-blur-sm border border-gray-200/50 dark:border-white/10 rounded-lg text-sm text-text-light dark:text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        >
          {commonTimezones.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>

        {/* Add button */}
        <button
          type="submit"
          disabled={!name.trim()}
          className="bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap text-sm font-medium transition-all shadow-sm hover:shadow"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </form>
    </div>
  );
}
